/**
 * Contas do modulo de funis -- todas em INTEIROS.
 *
 * O chip "Produto:" nao refaz requisicao: ele escolhe quais fatias entram na
 * soma. Este arquivo e o unico lugar que soma fatias, monta os degraus do
 * caminho, a faixa de resumo e as linhas da tabela; os componentes so
 * formatam. Nenhuma funcao aqui divide: proporcao e `{ part, whole }` e vira
 * texto so em `formatRatio`.
 */

import type {
  CreditChannel,
  Delta,
  DestaquesDaRegua,
  DispatchSummary,
  FunnelDailyPoint,
  FunnelKind,
  FunnelLead,
  FunnelOfferSlice,
  FunnelStep,
  Insight,
  OfferMetrics,
  OfferRow,
  PreviousPeriodMetrics,
  SinalDaOferta,
  TouchDefinition,
  TouchMetrics,
} from './types';
import { CREDIT_CHANNELS } from './types';
import { VOCABULARIO, rotuloDoAtraso } from './vocabulario';

export const TUDO = 'tudo';

function somarRegistro(a: Readonly<Record<CreditChannel, number>>, b: Readonly<Record<CreditChannel, number>>): Record<CreditChannel, number> {
  const saida: Record<CreditChannel, number> = { voice: 0, whatsapp: 0, email: 0, seller: 0 };
  for (const canal of CREDIT_CHANNELS) {
    saida[canal] = a[canal] + b[canal];
  }
  return saida;
}

function somarToque(a: TouchMetrics, b: TouchMetrics): TouchMetrics {
  return {
    touchId: a.touchId,
    measurable: a.measurable && b.measurable,
    queued: a.queued + b.queued,
    reached: a.reached + b.reached,
    delivered: a.delivered + b.delivered,
    opened: a.opened + b.opened,
    clicked: a.clicked + b.clicked,
    paid: a.paid + b.paid,
    paidByButton: a.paidByButton + b.paidByButton,
    paidAmountCents: a.paidAmountCents + b.paidAmountCents,
    replied: a.replied + b.replied,
    closedBySeller: a.closedBySeller + b.closedBySeller,
    closedBySellerAmountCents: a.closedBySellerAmountCents + b.closedBySellerAmountCents,
    unsubscribed: a.unsubscribed + b.unsubscribed,
    waitingNext: a.waitingNext + b.waitingNext,
    movedOn: a.movedOn + b.movedOn,
    endedWithoutReturn: a.endedWithoutReturn + b.endedWithoutReturn,
    failed: a.failed + b.failed,
    stuckAmountCents: a.stuckAmountCents + b.stuckAmountCents,
    voice:
      a.voice === null && b.voice === null
        ? null
        : {
            calls: (a.voice?.calls ?? 0) + (b.voice?.calls ?? 0),
            answered: (a.voice?.answered ?? 0) + (b.voice?.answered ?? 0),
            voicemail: (a.voice?.voicemail ?? 0) + (b.voice?.voicemail ?? 0),
            noAnswer: (a.voice?.noAnswer ?? 0) + (b.voice?.noAnswer ?? 0),
            failed: (a.voice?.failed ?? 0) + (b.voice?.failed ?? 0),
            paidAfterCall: (a.voice?.paidAfterCall ?? 0) + (b.voice?.paidAfterCall ?? 0),
          },
    email:
      a.email === null && b.email === null
        ? null
        : {
            sent: (a.email?.sent ?? 0) + (b.email?.sent ?? 0),
            fallback: (a.email?.fallback ?? 0) + (b.email?.fallback ?? 0),
            skipped: (a.email?.skipped ?? 0) + (b.email?.skipped ?? 0),
            opened: (a.email?.opened ?? 0) + (b.email?.opened ?? 0),
            clicked: (a.email?.clicked ?? 0) + (b.email?.clicked ?? 0),
            paid: (a.email?.paid ?? 0) + (b.email?.paid ?? 0),
          },
  };
}

function somarDia(a: FunnelDailyPoint, b: FunnelDailyPoint): FunnelDailyPoint {
  return {
    day: a.day,
    entered: a.entered + b.entered,
    recovered: a.recovered + b.recovered,
    recoveredAmountCents: a.recoveredAmountCents + b.recoveredAmountCents,
  };
}

/** Une listas alinhadas por indice; se uma for mais curta, o que sobra entra como esta. */
function somarAlinhado<T>(a: readonly T[], b: readonly T[], soma: (x: T, y: T) => T): readonly T[] {
  const tamanho = Math.max(a.length, b.length);
  const saida: T[] = [];
  for (let i = 0; i < tamanho; i += 1) {
    const x = a[i];
    const y = b[i];
    if (x !== undefined && y !== undefined) {
      saida.push(soma(x, y));
    } else if (x !== undefined) {
      saida.push(x);
    } else if (y !== undefined) {
      saida.push(y);
    }
  }
  return saida;
}

export function somarMetricas(a: OfferMetrics, b: OfferMetrics): OfferMetrics {
  return {
    checkouts: a.checkouts + b.checkouts,
    paidOnTime: a.paidOnTime + b.paidOnTime,
    entered: a.entered + b.entered,
    dueToday: a.dueToday + b.dueToday,
    returnedAlone: a.returnedAlone + b.returnedAlone,
    returnedAloneAmountCents: a.returnedAloneAmountCents + b.returnedAloneAmountCents,
    recovered: a.recovered + b.recovered,
    recoveredAmountCents: a.recoveredAmountCents + b.recoveredAmountCents,
    recoveredByChannel: somarRegistro(a.recoveredByChannel, b.recoveredByChannel),
    recoveredAmountByChannelCents: somarRegistro(a.recoveredAmountByChannelCents, b.recoveredAmountByChannelCents),
    carryOverRecovered: a.carryOverRecovered + b.carryOverRecovered,
    carryOverAmountCents: a.carryOverAmountCents + b.carryOverAmountCents,
    commissionCents: a.commissionCents + b.commissionCents,
    withSeller: a.withSeller + b.withSeller,
    unsubscribed: a.unsubscribed + b.unsubscribed,
    stillOut: a.stillOut + b.stillOut,
    stuckAmountCents: a.stuckAmountCents + b.stuckAmountCents,
    touches: somarAlinhado(a.touches, b.touches, somarToque),
    daily: somarAlinhado(a.daily, b.daily, somarDia),
    previous: somarAnterior(a.previous, b.previous),
  };
}

const ANTERIOR_ZERO: PreviousPeriodMetrics = { checkouts: 0, paidOnTime: 0, entered: 0, recovered: 0, recoveredAmountCents: 0, commissionCents: 0 };

function somarAnterior(a: PreviousPeriodMetrics | null, b: PreviousPeriodMetrics | null): PreviousPeriodMetrics | null {
  if (a === null && b === null) {
    return null;
  }
  const za = a ?? ANTERIOR_ZERO;
  const zb = b ?? ANTERIOR_ZERO;
  return {
    checkouts: za.checkouts + zb.checkouts,
    paidOnTime: za.paidOnTime + zb.paidOnTime,
    entered: za.entered + zb.entered,
    recovered: za.recovered + zb.recovered,
    recoveredAmountCents: za.recoveredAmountCents + zb.recoveredAmountCents,
    commissionCents: za.commissionCents + zb.commissionCents,
  };
}

export function metricasVazias(toques: readonly TouchDefinition[]): OfferMetrics {
  return {
    checkouts: 0,
    paidOnTime: 0,
    entered: 0,
    dueToday: 0,
    returnedAlone: 0,
    returnedAloneAmountCents: 0,
    recovered: 0,
    recoveredAmountCents: 0,
    recoveredByChannel: { voice: 0, whatsapp: 0, email: 0, seller: 0 },
    recoveredAmountByChannelCents: { voice: 0, whatsapp: 0, email: 0, seller: 0 },
    carryOverRecovered: 0,
    carryOverAmountCents: 0,
    commissionCents: 0,
    withSeller: 0,
    unsubscribed: 0,
    stillOut: 0,
    stuckAmountCents: 0,
    touches: toques.map((t) => ({
      touchId: t.id,
      measurable: true,
      queued: 0,
      reached: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      paid: 0,
      paidByButton: 0,
      paidAmountCents: 0,
      replied: 0,
      closedBySeller: 0,
      closedBySellerAmountCents: 0,
      unsubscribed: 0,
      waitingNext: 0,
      movedOn: 0,
      endedWithoutReturn: 0,
      failed: 0,
      stuckAmountCents: 0,
      voice: t.channel === 'voice' ? { calls: 0, answered: 0, voicemail: 0, noAnswer: 0, failed: 0, paidAfterCall: 0 } : null,
      email: t.email === null ? null : { sent: 0, fallback: 0, skipped: 0, opened: 0, clicked: 0, paid: 0 },
    })),
    daily: [],
    previous: null,
  };
}

// -----------------------------------------------------------------------------
// Comparacao com o periodo anterior
// -----------------------------------------------------------------------------

/** Delta entre dois inteiros. A unica divisao (bps) e para exibicao e fica guardada em inteiro. */
export function delta(atual: number, anterior: number): Delta {
  return {
    atual,
    anterior,
    diferenca: atual - anterior,
    bps: anterior === 0 ? null : Math.round(((atual - anterior) * 10_000) / anterior),
  };
}

/**
 * Taxa de recuperacao atual e anterior em bps, para mostrar "de 9,1% para
 * 11,4%". Nao dividimos taxa por taxa: "+25% na taxa" confunde com a
 * variacao dos recuperados.
 */
export function taxasParaComparar(m: OfferMetrics): { readonly atualBps: number | null; readonly anteriorBps: number | null } {
  const atualBps = m.entered === 0 ? null : Math.round((m.recovered * 10_000) / m.entered);
  const p = m.previous;
  const anteriorBps = p === null || p.entered === 0 ? null : Math.round((p.recovered * 10_000) / p.entered);
  return { atualBps, anteriorBps };
}

// -----------------------------------------------------------------------------
// Melhor e pior toque
// -----------------------------------------------------------------------------

/** Quem voltou por causa deste toque: pagou (botao, e-mail, ligacao) ou respondeu e a vendedora fechou. */
export function voltaramNoToque(t: TouchMetrics): number {
  return t.paid + t.closedBySeller;
}

/**
 * Compara conversao de dois toques SEM dividir: a/b > c/d <=> a*d > c*b para
 * b, d > 0. Devolve > 0 quando `x` converte melhor que `y`.
 */
function compararConversao(x: TouchMetrics, y: TouchMetrics): number {
  return voltaramNoToque(x) * y.reached - voltaramNoToque(y) * x.reached;
}

/**
 * Melhor e pior toque da janela. So entram toques mensuraveis, ligados e com
 * ao menos 10 pessoas alcancadas; com menos de dois candidatos nao ha o que
 * comparar e os dois saem `null` (um unico toque nao e "o melhor" de nada).
 */
export function destaquesDaRegua(m: OfferMetrics, toques: readonly TouchDefinition[]): DestaquesDaRegua {
  const candidatos = m.touches.filter((t, i) => t.measurable && t.reached >= 10 && toques[i]?.enabled === true);
  if (candidatos.length < 2) {
    return { melhor: null, pior: null };
  }
  let melhor = candidatos[0];
  let pior = candidatos[0];
  for (const t of candidatos) {
    if (melhor !== undefined && compararConversao(t, melhor) > 0) {
      melhor = t;
    }
    if (pior !== undefined && compararConversao(t, pior) < 0) {
      pior = t;
    }
  }
  if (melhor === undefined || pior === undefined || melhor.touchId === pior.touchId) {
    return { melhor: null, pior: null };
  }
  return { melhor: melhor.touchId, pior: pior.touchId };
}

/**
 * Comissao gerada por este toque (fee congelado da empresa) e, por venda,
 * quanto a empresa paga ao Converte Facil por recuperacao vinda dele. E o
 * "custo por recuperacao" do lojista dito do jeito que ele ve: comissao, nao
 * gasto. Divisao por `voltaram` so para exibicao, arredondada em centavos.
 */
export function comissaoDoToque(t: TouchMetrics, feeBps: number): { readonly totalCents: number; readonly porVendaCents: number | null } {
  const voltaram = voltaramNoToque(t);
  const totalCents = Math.floor(((t.paidAmountCents + t.closedBySellerAmountCents) * feeBps) / 10_000);
  return { totalCents, porVendaCents: voltaram === 0 ? null : Math.round(totalCents / voltaram) };
}

/** Soma as fatias que o filtro deixa passar. `TUDO` = todas. */
export function recortar(slices: readonly FunnelOfferSlice[], toques: readonly TouchDefinition[], offerId: string): OfferMetrics {
  const escolhidas = offerId === TUDO ? slices : slices.filter((s) => s.offer.id === offerId);
  return escolhidas.reduce<OfferMetrics>((acc, s) => somarMetricas(acc, s.metrics), metricasVazias(toques));
}

export function resumoDoDisparo(m: OfferMetrics, toques: readonly TouchDefinition[]): DispatchSummary {
  let whatsappSent = 0;
  let calls = 0;
  let emailsSent = 0;
  let emailsFallback = 0;
  let emailsOpened = 0;
  let clicked = 0;
  let queuedNow = 0;
  let leftBecausePaid = 0;
  let leftBecauseReplied = 0;
  let unsubscribed = 0;
  m.touches.forEach((t, i) => {
    const def = toques[i];
    if (def?.channel === 'voice') {
      calls += t.voice?.calls ?? 0;
    } else {
      whatsappSent += t.reached;
    }
    emailsSent += t.email?.sent ?? 0;
    emailsFallback += t.email?.fallback ?? 0;
    emailsOpened += t.email?.opened ?? 0;
    clicked += t.clicked;
    queuedNow += t.queued;
    leftBecausePaid += t.paid;
    leftBecauseReplied += t.replied;
    unsubscribed += t.unsubscribed;
  });
  return {
    entered: m.entered,
    whatsappSent,
    calls,
    emailsSent,
    emailsFallback,
    emailsOpened,
    clicked,
    queuedNow,
    leftBecausePaid,
    leftBecauseReplied,
    unsubscribed,
  };
}

export function degrausDoCaminho(kind: FunnelKind, m: OfferMetrics, formatBRL: (cents: number) => string, formatN: (n: number) => string): readonly FunnelStep[] {
  const v = VOCABULARIO[kind];
  const [d1, d2, d3] = v.degraus;
  const canais = m.recoveredByChannel;

  const detalheEntrada =
    kind === 'carrinho_abandonado'
      ? `de ${formatN(m.checkouts)} que iniciaram o checkout · ${formatN(m.paidOnTime)} pagaram na hora`
      : `${formatN(m.dueToday)} vencem ainda hoje`;

  const partesRecuperado: string[] = [formatBRL(m.recoveredAmountCents)];
  if (canais.voice > 0) {
    partesRecuperado.push(`${formatN(canais.voice)} pela ligação`);
  }
  partesRecuperado.push(`${formatN(canais.whatsapp + canais.email)} pelo botão`);
  partesRecuperado.push(`${formatN(canais.seller)} pela vendedora`);

  const detalheFora =
    kind === 'carrinho_abandonado'
      ? `≈ ${formatBRL(m.stuckAmountCents)} parados · ${formatN(m.withSeller)} com o comercial · ${formatN(m.unsubscribed)} pediram pra sair`
      : `≈ ${formatBRL(m.stuckAmountCents)} parados · ${formatN(m.withSeller)} com o comercial`;

  return [
    { key: 'entered', eyebrow: d1.eyebrow, value: m.entered, label: d1.label, detail: detalheEntrada, tone: 'accent', ratio: null },
    { key: 'paid', eyebrow: d2.eyebrow, value: m.recovered, label: d2.label, detail: partesRecuperado.join(' · '), tone: 'good', ratio: { part: m.recovered, whole: m.entered } },
    { key: 'out', eyebrow: d3.eyebrow, value: m.stillOut, label: d3.label, detail: detalheFora, tone: 'critical', ratio: { part: m.stillOut, whole: m.entered } },
  ];
}

/**
 * Sinal de uma oferta comparada ao conjunto, sem dividir:
 *  - `oportunidade`: parados acima da media das ofertas (em dinheiro) E taxa de
 *    recuperacao >= a geral. Tem gente parada numa oferta que costuma voltar.
 *  - `atencao`: abandono acima da media (carrinho) ou volume acima da media
 *    (expirados) E taxa de recuperacao abaixo da metade da geral. A regua nao
 *    esta dando conta dessa oferta.
 * Exige ao menos 10 entradas na oferta e 2 ofertas no conjunto: com menos que
 * isso qualquer sinal seria ruido.
 */
function sinalDaOferta(kind: FunnelKind, m: OfferMetrics, total: OfferMetrics, quantidade: number): SinalDaOferta | null {
  if (quantidade < 2 || m.entered < 10 || total.entered === 0) {
    return null;
  }
  // taxa da oferta >= taxa geral  <=>  m.recovered * total.entered >= total.recovered * m.entered
  const taxaMaiorOuIgual = m.recovered * total.entered >= total.recovered * m.entered;
  const taxaAbaixoDaMetade = m.recovered * total.entered * 2 < total.recovered * m.entered;
  const paradoAcimaDaMedia = m.stuckAmountCents * quantidade > total.stuckAmountCents;
  if (paradoAcimaDaMedia && taxaMaiorOuIgual && m.stillOut > 0) {
    return 'oportunidade';
  }
  const volumeAcimaDaMedia =
    kind === 'carrinho_abandonado'
      ? total.checkouts > 0 && m.checkouts > 0 && m.entered * total.checkouts > total.entered * m.checkouts
      : m.entered * quantidade > total.entered;
  if (volumeAcimaDaMedia && taxaAbaixoDaMetade) {
    return 'atencao';
  }
  return null;
}

export function linhasPorOferta(kind: FunnelKind, slices: readonly FunnelOfferSlice[], toques: readonly TouchDefinition[]): readonly OfferRow[] {
  const total = recortar(slices, toques, TUDO);
  return slices
    .map((s) => ({
      offer: s.offer,
      checkouts: s.metrics.checkouts,
      paidOnTime: s.metrics.paidOnTime,
      entered: s.metrics.entered,
      recovered: s.metrics.recovered,
      recoveredAmountCents: s.metrics.recoveredAmountCents,
      commissionCents: s.metrics.commissionCents,
      returnedAlone: s.metrics.returnedAlone,
      withSeller: s.metrics.withSeller,
      stillOut: s.metrics.stillOut,
      stuckAmountCents: s.metrics.stuckAmountCents,
      sinal: sinalDaOferta(kind, s.metrics, total, slices.length),
    }))
    .sort((a, b) => b.entered - a.entered);
}

// -----------------------------------------------------------------------------
// "O que fazer agora"
// -----------------------------------------------------------------------------

/**
 * Insights automaticos, em ordem de urgencia. Cada um nasce de uma conta
 * sobre os inteiros da janela e aponta para um lugar da propria pagina. A
 * lista e curta de proposito (ate 5): mais que isso deixa de ser "o que fazer
 * agora" e vira relatorio.
 *
 * Regras que evitam insight falso:
 *  - nada e dito sobre taxa com menos de 10 pessoas na base;
 *  - a projecao usa a taxa DA PROPRIA JANELA sobre quem ainda esta parado, e
 *    diz que e estimativa;
 *  - "leads de alto valor" sao os que estao NA REGUA agora, acima do ticket
 *    medio de quem entrou -- nao um numero magico.
 */
export function gerarInsights(
  kind: FunnelKind,
  m: OfferMetrics,
  rows: readonly OfferRow[],
  leads: readonly FunnelLead[],
  toques: readonly TouchDefinition[],
  formatBRL: (cents: number) => string,
  formatN: (n: number) => string,
  formatRatio: (part: number, whole: number) => string,
): readonly Insight[] {
  const v = VOCABULARIO[kind];
  const saida: Insight[] = [];
  const expirados = kind === 'expirados';

  // 1. Quem respondeu e a vendedora ainda nao fechou: a janela de 7 dias esta correndo.
  if (m.withSeller > 0) {
    const valorComComercial = leads.filter((l) => l.status === 'com_comercial').reduce((acc, l) => acc + l.amountCents, 0);
    const um = m.withSeller === 1;
    saida.push({
      key: 'com-comercial',
      tom: 'alerta',
      titulo: `${formatN(m.withSeller)} ${um ? 'pessoa respondeu e está' : 'pessoas responderam e estão'} com o comercial`,
      texto: `A régua parou para ${um ? 'ela' : 'elas'} e a venda só conta como recuperada se fechar em até 7 dias depois da resposta (ADR-001).${
        valorComComercial > 0 ? ` Só na lista desta tela são ${formatBRL(valorComComercial)} esperando retorno.` : ''
      }`,
      acao: { kind: 'leads', status: 'com_comercial' },
      rotuloAcao: 'Ver quem está com o comercial',
    });
  }

  // 2. Oferta com sinal de oportunidade (maior valor parado entre as sinalizadas).
  const oportunidade = rows.filter((r) => r.sinal === 'oportunidade').sort((a, b) => b.stuckAmountCents - a.stuckAmountCents)[0];
  if (oportunidade !== undefined) {
    saida.push({
      key: `oportunidade-${oportunidade.offer.id}`,
      tom: 'oportunidade',
      titulo: `${oportunidade.offer.name}: ${formatBRL(oportunidade.stuckAmountCents)} parados ${expirados ? 'num plano' : 'numa oferta'} que volta bem`,
      texto: `${formatN(oportunidade.stillOut)} ${expirados ? 'ainda fora' : 'ainda parados'} e taxa de recuperação de ${formatRatio(oportunidade.recovered, oportunidade.entered)} (geral: ${formatRatio(
        m.recovered,
        m.entered,
      )}). Vale um toque a mais ou uma condição especial só para ${expirados ? 'este plano' : 'esta oferta'}.`,
      acao: { kind: 'oferta', offerId: oportunidade.offer.id },
      rotuloAcao: `Recortar por ${expirados ? 'este plano' : 'esta oferta'}`,
    });
  }

  // 3. Oferta com sinal de atencao (maior volume entre as sinalizadas).
  const atencao = rows.filter((r) => r.sinal === 'atencao').sort((a, b) => b.entered - a.entered)[0];
  if (atencao !== undefined) {
    saida.push({
      key: `atencao-${atencao.offer.id}`,
      tom: 'alerta',
      titulo: `${atencao.offer.name} ${expirados ? 'vence muito e quase não volta' : 'abandona muito e quase não volta'}`,
      texto: expirados
        ? `${formatN(atencao.entered)} venceram e só ${formatRatio(atencao.recovered, atencao.entered)} voltaram (geral: ${formatRatio(m.recovered, m.entered)}). O problema pode ser o plano, não a régua — vale olhar preço e oferta de volta.`
        : `Taxa de abandono de ${formatRatio(atencao.entered, atencao.checkouts)} e só ${formatRatio(atencao.recovered, atencao.entered)} recuperados (geral: ${formatRatio(m.recovered, m.entered)}). Confira o checkout desta oferta antes de mexer na régua.`,
      acao: { kind: 'oferta', offerId: atencao.offer.id },
      rotuloAcao: `Recortar por ${expirados ? 'este plano' : 'esta oferta'}`,
    });
  }

  // 4. Pior e melhor toque.
  const destaques = destaquesDaRegua(m, toques);
  const indicePior = destaques.pior === null ? -1 : m.touches.findIndex((t) => t.touchId === destaques.pior);
  const indiceMelhor = destaques.melhor === null ? -1 : m.touches.findIndex((t) => t.touchId === destaques.melhor);
  const pior = m.touches[indicePior];
  const melhor = m.touches[indiceMelhor];
  const defPior = toques[indicePior];
  const defMelhor = toques[indiceMelhor];
  if (pior !== undefined && defPior !== undefined && melhor !== undefined && defMelhor !== undefined) {
    const semBotao = defPior.buttons.length === 0 && defPior.channel === 'whatsapp';
    saida.push({
      key: `toque-${defPior.id}`,
      tom: 'alerta',
      titulo: `${rotuloDoAtraso(kind, defPior.delayMinutes)} é o toque que menos converte`,
      texto: `${formatRatio(voltaramNoToque(pior), pior.reached)} de quem recebeu voltou, contra ${formatRatio(voltaramNoToque(melhor), melhor.reached)} no ${rotuloDoAtraso(
        kind,
        defMelhor.delayMinutes,
      )}. Revise a mensagem${semBotao ? ' e considere um botão de link' : ''}; o texto do melhor toque é o ponto de partida.`,
      acao: { kind: 'config', touchId: defPior.id },
      rotuloAcao: 'Ajustar este toque',
    });
  }

  // 5. Leads de alto valor ainda na regua (acima do ticket medio de quem entrou e nao teve desfecho).
  const naRegua = leads.filter((l) => l.status === 'na_regua');
  if (naRegua.length > 0 && m.stillOut + m.recovered > 0) {
    const ticketMedioCents = Math.round((m.stuckAmountCents + m.recoveredAmountCents) / (m.stillOut + m.recovered));
    const altoValor = naRegua.filter((l) => l.amountCents > ticketMedioCents).sort((a, b) => b.amountCents - a.amountCents);
    if (altoValor.length > 0) {
      const soma = altoValor.reduce((acc, l) => acc + l.amountCents, 0);
      saida.push({
        key: 'alto-valor',
        tom: 'oportunidade',
        titulo: `${formatN(altoValor.length)} ${altoValor.length === 1 ? 'lead de alto valor está' : 'leads de alto valor estão'} na régua agora`,
        texto: `${formatBRL(soma)} acima do ticket médio (${formatBRL(ticketMedioCents)}), ainda sem desfecho. Um contato humano do comercial, fora da régua, costuma valer o esforço nesses casos.`,
        acao: { kind: 'leads', status: 'na_regua' },
        rotuloAcao: 'Ver quem está na régua',
      });
    }
  }

  // 6. Projecao: quantos dos parados ainda devem voltar, na taxa desta janela.
  if (m.stillOut > 0 && m.entered >= 10 && m.recovered > 0) {
    const esperados = Math.round((m.stillOut * m.recovered) / m.entered);
    const esperadosCents = Math.round((m.stuckAmountCents * m.recovered) / m.entered);
    if (esperados > 0) {
      saida.push({
        key: 'projecao',
        tom: 'projecao',
        titulo: `Projeção: mais ${formatN(esperados)} ${expirados ? 'voltas' : 'recuperações'} entre quem ainda está ${expirados ? 'fora' : 'parado'}`,
        texto: `Estimativa na taxa desta janela (${formatRatio(m.recovered, m.entered)}) sobre ${formatN(m.stillOut)} ${expirados ? 'ainda fora' : 'ainda parados'}: cerca de ${formatBRL(
          esperadosCents,
        )} a recuperar. É ritmo, não promessa — muda com cada toque que sai.`,
        acao: null,
        rotuloAcao: null,
      });
    }
  }

  // 7. Tudo bem: nada urgente.
  if (saida.length === 0 && m.entered > 0) {
    saida.push({
      key: 'ok',
      tom: 'bom',
      titulo: 'Nada urgente nesta janela',
      texto: `${formatN(m.recovered)} ${v.recuperadosKpi.toLowerCase()} de ${formatN(m.entered)}. Os avisos aparecem aqui quando alguém responde e fica com o comercial, quando uma oferta destoa das outras ou quando um toque converte bem abaixo dos demais.`,
      acao: null,
      rotuloAcao: null,
    });
  }

  return saida.slice(0, 5);
}

/** Agrupa a serie diaria em semanas quando a janela e longa, para o grafico caber. */
export function agruparSerie(daily: readonly FunnelDailyPoint[]): { readonly labels: readonly string[]; readonly entered: readonly number[]; readonly recovered: readonly number[] } {
  const tamanhoDoGrupo = daily.length <= 45 ? 1 : daily.length <= 200 ? 7 : 30;
  const fmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });
  const labels: string[] = [];
  const entered: number[] = [];
  const recovered: number[] = [];
  for (let i = 0; i < daily.length; i += tamanhoDoGrupo) {
    const grupo = daily.slice(i, i + tamanhoDoGrupo);
    const primeiro = grupo[0];
    if (primeiro === undefined) {
      continue;
    }
    labels.push(fmt.format(new Date(`${primeiro.day}T12:00:00.000Z`)));
    entered.push(grupo.reduce((acc, p) => acc + p.entered, 0));
    recovered.push(grupo.reduce((acc, p) => acc + p.recovered, 0));
  }
  return { labels, entered, recovered };
}
